import React, { useState, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import { Link } from "react-router-dom";
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useToggleDepartmentStatus,
  useDeleteDepartment,
  useCompanies,
} from "../../../api/queries/organizationQueries";
import SafeDeleteDialog from "../../../components/ui/SafeDeleteDialog";
import { getApiErrorMessage } from "../../../utils/apiError";
import { useCanModify } from "../../../hooks/usePermissions";

const Department = () => {
  // View-only roles see the list without create / edit / status actions
  const canModify = useCanModify("company-setup", { company: true });
  // Departments are per company (companyId null = shared). Company + active filters run on the server.
  const [companyFilter, setCompanyFilter] = useState(""); // "" = shared + my company tree
  const [statusFilter, setStatusFilter] = useState("All");
  const { data: departments = [] } = useDepartments(
    companyFilter || undefined,
    {
      active: statusFilter === "All" ? undefined : statusFilter === "Active",
    },
  );
  const { data: companies = [] } = useCompanies();
  const companyName = (d) =>
    d.company?.companyName ||
    companies.find((c) => c.id === d.companyId)?.companyName ||
    (d.companyId ? "—" : null);
  const deleteDepartment = useDeleteDepartment();
  const [deleting, setDeleting] = useState(null);
  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const toggleDepartmentStatus = useToggleDepartmentStatus();
  const isSaving = createDepartment.isPending || updateDepartment.isPending;

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDept, setPendingDept] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    departmentName: "",
    description: "",
    isActive: true,
    companyId: "",
  });

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "departmentName",
    direction: "asc",
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, companyFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-down ms-2 text-primary fs-070"></i>
    ) : (
      <i className="bi bi-sort-up ms-2 text-primary fs-070"></i>
    );
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({
      id: "",
      departmentName: "",
      description: "",
      isActive: true,
      companyId: companyFilter || "",
    });
    setShowModal(true);
  };

  const openEditModal = (dept) => {
    setIsEditMode(true);
    setFormData({
      id: dept.id,
      departmentName: dept.departmentName,
      description: dept.description || "",
      isActive: dept.isActive,
      companyId: dept.companyId || dept.company?.id || "",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        await updateDepartment.mutateAsync({ id: formData.id, data: formData });
        toast.success(`${formData.departmentName} updated!`);
      } else {
        await createDepartment.mutateAsync(formData);
        toast.success(`${formData.departmentName} created!`);
      }
      setShowModal(false);
    } catch (err) {
      console.error("Department Save Error:", err);
      toast.error(getApiErrorMessage(err, "Failed to save"));
    }
  };

  const handleStatusToggle = (dept) => {
    if (dept.isActive) {
      setPendingDept(dept);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(dept);
    }
  };

  const executeStatusUpdate = async (dept) => {
    try {
      await toggleDepartmentStatus.mutateAsync({
        id: dept.id,
        currentStatus: dept.isActive,
      });
      toast.success(`${dept.departmentName} updated!`);
      setShowConfirm(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Update failed"));
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    let items = Array.isArray(departments) ? [...departments] : [];
    items = items.filter((item) => {
      const matchesSearch =
        (item.departmentName || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (item.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });

    items.sort((a, b) => {
      const valA = String(a[sortConfig.key] || "").toLowerCase();
      const valB = String(b[sortConfig.key] || "").toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return items;
  }, [departments, searchTerm, sortConfig]);

  const totalItems = processedData.length;
  const currentData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Departments</h1>
            <p className="text-muted small mb-0">Manage organizational units</p>
          </div>
          {canModify && (
            <button
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={openAddModal}
            >
              <i className="bi bi-plus-lg me-2"></i>Add New
            </button>
          )}
        </div>

        {/* Filters Row */}
        <div className="row g-2 mb-4">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control filter-input"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              aria-label="Company"
            >
              <option value="">All my companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName} (+ shared)
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Glass Table Container */}
        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header sortable">
                <tr>
                  <th
                    className="ps-4 py-3 border-0"
                    onClick={() => requestSort("departmentName")}
                  >
                    DEPARTMENT NAME {getSortIcon("departmentName")}
                  </th>
                  <th className="py-3 border-0">COMPANY</th>
                  <th className="py-3 border-0 text-center">STATUS</th>
                  <th className="pe-4 py-3 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {currentData.map((item) => (
                  <tr key={item.id} className="align-middle">
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark fs-085">
                        {item.departmentName}
                      </div>
                      <div className="text-muted fs-075">
                        {item.description || "No description"}
                      </div>
                    </td>
                    <td className="fs-080 text-slate-700">
                      {companyName(item) || (
                        <span className="chip chip--sm chip--alt">Shared</span>
                      )}
                    </td>
                    <td className="text-center">
                      <span
                        className={`status-pill status-pill--${item.isActive ? "green" : "gray"}`}
                      >
                        {toCamelCase(item.isActive ? "Active" : "Inactive")}
                      </span>
                    </td>
                    <td className="pe-4 text-end">
                      {canModify ? (
                        <>
                          <div className="d-flex justify-content-end gap-2">
                            <button
                              className="btn btn-sm btn-light border"
                              onClick={() => openEditModal(item)}
                            >
                              Edit
                            </button>
                            <button
                              className={`btn btn-sm ${item.isActive ? "btn-outline-danger" : "btn-outline-success"}`}
                              onClick={() => handleStatusToggle(item)}
                            >
                              {item.isActive ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setDeleting(item)}
                              title="Delete"
                              aria-label={`Delete ${item.departmentName}`}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="fs-070 text-slate-400">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={currentPage}
            limit={itemsPerPage}
            total={totalItems}
            onPageChange={setCurrentPage}
            onLimitChange={(v) => {
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
            pageSizes={[10, 25, 50]}
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <Dialog
          title="Confirm Deactivation"
          onClose={() => setShowConfirm(false)}
          size="sm"
        >
          <div className="text-start">
            <p className="text-muted small">
              Are you sure you want to deactivate{" "}
              <strong>{pendingDept?.departmentName}</strong>?
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger profile-btn"
                onClick={() => executeStatusUpdate(pendingDept)}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Form Modal */}
      {showModal && (
        <Dialog
          title={<>{isEditMode ? "Edit" : "Add"} Department</>}
          onClose={() => setShowModal(false)}
          size="md"
        >
          <form onSubmit={handleSave}>
            <div className="mb-3">
              <label className="profile-label">Department Name</label>
              <input
                type="text"
                className="form-control"
                required
                value={formData.departmentName}
                onChange={(e) =>
                  setFormData({ ...formData, departmentName: e.target.value })
                }
              />
            </div>
            <div className="mb-3">
              <label htmlFor="dept-company" className="profile-label">
                Company
              </label>
              <select
                id="dept-company"
                className="form-select"
                value={formData.companyId}
                onChange={(e) =>
                  setFormData({ ...formData, companyId: e.target.value })
                }
              >
                <option value="">All companies (shared)</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName}
                  </option>
                ))}
              </select>
              <div className="fs-070 text-slate-500 mt-1">
                Names must be unique within the company.
              </div>
            </div>
            <div className="mb-3">
              <label className="profile-label">Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              ></textarea>
            </div>
            <div className="form-check form-switch mb-4">
              <input
                className="form-check-input"
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
              <label className="form-check-label fs-085">Active Status</label>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary profile-btn"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Department"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
      {deleting && (
        <SafeDeleteDialog
          title="Delete department?"
          name={deleting.departmentName}
          isActive={deleting.isActive}
          onDelete={() => deleteDepartment.mutateAsync(deleting.id)}
          onDeactivate={() =>
            toggleDepartmentStatus.mutateAsync({
              id: deleting.id,
              currentStatus: true,
            })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </Layout>
  );
};

export default Department;
